import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Popover,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  IconButton,
  Fade,
  TextField,
  Alert,
  Snackbar,
  Tooltip,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import StorefrontIcon from '@mui/icons-material/Storefront';
import UndoIcon from '@mui/icons-material/Undo';
import { useTranslation } from 'react-i18next';
import './ImproveTextWithAI.css';

const AI_API_URL = process.env.REACT_APP_AI_TEXT_URL || 'http://localhost:3001';
const COACHMARK_STORAGE_KEY = 'ai-improve-coachmark-seen';
const UNDO_TIMEOUT_MS = 6000;

const parseImprovedText = (data) => {
  if (typeof data === 'string') return data;
  if (data?.data?.improvedText) return data.data.improvedText;
  if (data?.improvedText) return data.improvedText;
  if (data?.text) return data.text;
  if (data?.improved_text) return data.improved_text;
  if (data?.result) return data.result;
  if (data?.message) return data.message;
  return null;
};

const parseApiError = async (response, t) => {
  try {
    const data = await response.json();

    if (response.status === 401) {
      return data?.detail || t('aiImprove.unauthorizedError');
    }

    return data?.detail || data?.message || t('aiImprove.requestError');
  } catch {
    if (response.status === 401) {
      return t('aiImprove.unauthorizedError');
    }

    return t('aiImprove.requestError');
  }
};

const ImproveTextWithAI = ({
  text,
  onApply,
  disabled = false,
  isAlreadyImproved = false,
  onAiApplied,
  onAiUndone,
  customerId,
  token,
}) => {
  const { t } = useTranslation();
  const undoTimerRef = useRef(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [mode, setMode] = useState('suporte');
  const [preview, setPreview] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [allowReImprove, setAllowReImprove] = useState(false);
  const [showCoachmark, setShowCoachmark] = useState(false);
  const [undoSnackbar, setUndoSnackbar] = useState({ open: false, previousText: '' });

  const isOpen = Boolean(anchorEl);
  const hasPreview = Boolean(preview);
  const isReImproveBlocked = isAlreadyImproved && !allowReImprove;

  useEffect(() => {
    if (disabled) {
      setShowCoachmark(false);
      return;
    }

    const hasSeenCoachmark = localStorage.getItem(COACHMARK_STORAGE_KEY);
    if (!hasSeenCoachmark) {
      setShowCoachmark(true);
    }
  }, [disabled]);

  useEffect(() => () => {
    if (undoTimerRef.current) {
      clearTimeout(undoTimerRef.current);
    }
  }, []);

  const dismissCoachmark = () => {
    localStorage.setItem(COACHMARK_STORAGE_KEY, 'true');
    setShowCoachmark(false);
  };

  const handleOpen = (event) => {
    dismissCoachmark();
    setAnchorEl(event.currentTarget);
    setPreview('');
    setError('');
    setAllowReImprove(false);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setPreview('');
    setError('');
    setIsLoading(false);
    setAllowReImprove(false);
  };

  const improveText = useCallback(async () => {
    const trimmed = text?.trim();
    if (!trimmed) {
      setError(t('aiImprove.emptyText'));
      return;
    }

    setIsLoading(true);
    setError('');
    setPreview('');

    try {
      const response = await fetch(`${AI_API_URL}/text/improve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'entry_customer_id': String(customerId),
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ text: trimmed, mode }),
      });

      if (!response.ok) {
        const errorMessage = await parseApiError(response, t);
        throw new Error(errorMessage);
      }

      const data = await response.json();
      const improved = parseImprovedText(data);

      if (!improved) {
        throw new Error(t('aiImprove.invalidResponse'));
      }

      setPreview(improved);
    } catch (err) {
      setError(err.message || t('aiImprove.requestError'));
    } finally {
      setIsLoading(false);
    }
  }, [text, mode, t, customerId, token]);

  const handleApply = () => {
    const trimmedPreview = preview?.trim();
    if (!trimmedPreview) return;

    const previousText = text?.trim() || '';
    onApply(trimmedPreview);
    onAiApplied?.();

    if (undoTimerRef.current) {
      clearTimeout(undoTimerRef.current);
    }

    setUndoSnackbar({ open: true, previousText });
    undoTimerRef.current = setTimeout(() => {
      setUndoSnackbar((current) => ({ ...current, open: false }));
    }, UNDO_TIMEOUT_MS);

    handleClose();
  };

  const handleUndo = () => {
    if (undoTimerRef.current) {
      clearTimeout(undoTimerRef.current);
    }

    onApply(undoSnackbar.previousText);
    onAiUndone?.();
    setUndoSnackbar({ open: false, previousText: '' });
  };

  const handleModeChange = (_, newMode) => {
    if (newMode) {
      setMode(newMode);
      setPreview('');
      setError('');
    }
  };

  const triggerButton = (
    <Button
      variant="outlined"
      size="small"
      startIcon={<AutoAwesomeIcon sx={{ fontSize: 16 }} />}
      onClick={handleOpen}
      disabled={disabled}
      className="ai-improve-trigger"
    >
      {t('aiImprove.button')}
    </Button>
  );

  return (
    <>
      {showCoachmark ? (
        <Tooltip
          title={t('aiImprove.coachmark')}
          open
          placement="top"
          arrow
          disableFocusListener
          disableHoverListener
          disableTouchListener
        >
          {triggerButton}
        </Tooltip>
      ) : (
        triggerButton
      )}

      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ className: 'ai-improve-popover' }}
        TransitionComponent={Fade}
        transitionDuration={200}
      >
        <Box className="ai-improve-panel">
          <Box className="ai-improve-header">
            <Box className="ai-improve-header-content">
              <Box className="ai-improve-icon-wrap">
                <AutoAwesomeIcon fontSize="small" />
              </Box>
              <Box>
                <Typography variant="subtitle2" className="ai-improve-title">
                  {t('aiImprove.title')}
                </Typography>
                <Typography variant="caption" className="ai-improve-subtitle">
                  {t('aiImprove.subtitle')}
                </Typography>
              </Box>
            </Box>
            <IconButton size="small" onClick={handleClose} className="ai-improve-close">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <Box className="ai-improve-body">
            <Typography variant="caption" className="ai-improve-label">
              {t('aiImprove.modeLabel')}
            </Typography>
            <ToggleButtonGroup
              value={mode}
              exclusive
              onChange={handleModeChange}
              fullWidth
              size="small"
              className="ai-improve-mode-group"
            >
              <ToggleButton value="suporte" className="ai-improve-mode-btn">
                <Box className="ai-improve-mode-option">
                  <Box className="ai-improve-mode-option-header">
                    <SupportAgentIcon sx={{ fontSize: 16 }} />
                    {t('aiImprove.modeSupport')}
                  </Box>
                  <Typography variant="caption" className="ai-improve-mode-hint">
                    {t('aiImprove.modeSupportHint')}
                  </Typography>
                </Box>
              </ToggleButton>
              <ToggleButton value="comercial" className="ai-improve-mode-btn">
                <Box className="ai-improve-mode-option">
                  <Box className="ai-improve-mode-option-header">
                    <StorefrontIcon sx={{ fontSize: 16 }} />
                    {t('aiImprove.modeCommercial')}
                  </Box>
                  <Typography variant="caption" className="ai-improve-mode-hint">
                    {t('aiImprove.modeCommercialHint')}
                  </Typography>
                </Box>
              </ToggleButton>
            </ToggleButtonGroup>

            {isReImproveBlocked && (
              <Alert severity="warning" className="ai-improve-warning">
                {t('aiImprove.alreadyImprovedWarning')}
              </Alert>
            )}

            <Box className="ai-improve-original">
              <Typography variant="caption" className="ai-improve-label">
                {isAlreadyImproved && !hasPreview
                  ? t('aiImprove.alreadyImprovedLabel')
                  : t('aiImprove.originalLabel')}
              </Typography>
              <Typography variant="body2" className="ai-improve-original-text">
                {text?.trim() || t('aiImprove.noText')}
              </Typography>
            </Box>

            {isLoading && (
              <Box className="ai-improve-loading">
                <CircularProgress size={32} thickness={4} className="ai-improve-spinner" />
                <Typography variant="body2" className="ai-improve-loading-text">
                  {t('aiImprove.loading')}
                </Typography>
                <Typography variant="caption" className="ai-improve-loading-hint">
                  {t('aiImprove.loadingHint')}
                </Typography>
              </Box>
            )}

            {!isLoading && hasPreview && (
              <Box className="ai-improve-preview">
                <Typography variant="caption" className="ai-improve-label">
                  {t('aiImprove.previewLabel')}
                </Typography>
                <Typography variant="caption" className="ai-improve-preview-hint">
                  {t('aiImprove.previewEditHint')}
                </Typography>
                <TextField
                  multiline
                  minRows={3}
                  maxRows={8}
                  fullWidth
                  value={preview}
                  onChange={(e) => setPreview(e.target.value)}
                  className="ai-improve-preview-field"
                  InputProps={{ className: 'ai-improve-preview-input' }}
                />
              </Box>
            )}

            {error && (
              <Typography variant="caption" className="ai-improve-error">
                {error}
              </Typography>
            )}

            <Box className="ai-improve-actions">
              {!hasPreview ? (
                isReImproveBlocked ? (
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => setAllowReImprove(true)}
                    className="ai-improve-secondary-btn"
                  >
                    {t('aiImprove.reImproveConfirm')}
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={improveText}
                    disabled={isLoading || !text?.trim()}
                    startIcon={isLoading ? null : <AutoAwesomeIcon />}
                    className="ai-improve-primary-btn"
                  >
                    {isLoading ? t('aiImprove.loading') : t('aiImprove.improve')}
                  </Button>
                )
              ) : (
                <>
                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={improveText}
                    disabled={isLoading}
                    className="ai-improve-secondary-btn"
                  >
                    {t('aiImprove.regenerate')}
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleApply}
                    disabled={isLoading || !preview?.trim()}
                    className="ai-improve-primary-btn"
                  >
                    {t('aiImprove.useMessage')}
                  </Button>
                </>
              )}
            </Box>
          </Box>
        </Box>
      </Popover>

      <Snackbar
        open={undoSnackbar.open}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        className="ai-improve-undo-snackbar"
      >
        <Alert
          severity="success"
          variant="filled"
          className="ai-improve-undo-alert"
          action={
            <Button
              color="inherit"
              size="small"
              startIcon={<UndoIcon sx={{ fontSize: 16 }} />}
              onClick={handleUndo}
              className="ai-improve-undo-btn"
            >
              {t('aiImprove.undoAction')}
            </Button>
          }
        >
          {t('aiImprove.undoMessage')}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ImproveTextWithAI;
